<script setup lang="ts">
// A caster input's WebRTC connection (DetailPanel): who is connected and
// how, the round trip, and what the link lost: packets resent in time are
// SRT-like recovery working, those lost for good are missing in the picture
// or sound. From the node's gateway (internal/caster), once a second.
// Invite links (central's) are listed here with their links to copy again,
// made and revoked; local links (the node's own, for its network) below.
import Figure from './Figure.vue'
import CasterLocalLinks from './CasterLocalLinks.vue'
import { computed, ref, watch } from 'vue'
import { api, central, nodeId, type CasterInvite, type CasterStats, type Input } from '../../api'
import { ago } from '../../util'
import { useNode } from '../../store'
import CasterControl from './CasterControl.vue'
import TurnCheck from './TurnCheck.vue'
import { returnChoice, returnLeaders, withReturnChoice } from '../../caster'

// peak: the input's channel peaks; the caster's microphone is channels 1+2
const props = defineProps<{
    st?: CasterStats
    error?: string
    inputId: string
    peak?: number[]
    returnEncoder?: string // what encodes the return feed (InputStatus.return_encoder)
    returnSharedWith?: string[] // the other caster inputs on that encode
}>()
const node = useNode()
const invites = ref<CasterInvite[] | null>(null)
const forWho = ref('')
const days = ref(3)
async function loadInvites() {
    if (!nodeId) return
    try {
        invites.value = (await central.casterInvites(nodeId)).filter((i) => i.input_id === props.inputId)
    } catch {
        invites.value = null
    }
}
watch(
    () => props.inputId,
    () => void loadInvites(),
    { immediate: true },
)
async function invite() {
    try {
        const inv = await central.createCasterInvite(nodeId, {
            input_id: props.inputId,
            name: forWho.value.trim(),
            hours: days.value * 24,
        })
        forWho.value = ''
        await node.copy(inv.url)
        await loadInvites()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
async function revoke(i: CasterInvite) {
    try {
        await central.revokeCasterInvite(nodeId, i.id)
        await loadInvites()
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    }
}
// the expiry with its day: links last days
const until = (t: string) =>
    new Date(t).toLocaleString(undefined, {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
    })
const usable = (i: CasterInvite) => !i.revoked_at && Date.parse(i.expires_at) > node.now
const count = (n: number) => n.toLocaleString()
const lostForGood = computed(() => (props.st ? props.st.video.lost_for_good + props.st.audio.lost_for_good : 0))
const resent = computed(() =>
    props.st ? props.st.video.reordered + props.st.audio.reordered + (props.st.audio.from_redundancy ?? 0) : 0,
)
const maxHold = computed(() => (props.st ? Math.max(props.st.video.max_hold_ms, props.st.audio.max_hold_ms) : 0))
const pct = (n: number) => {
    const all = props.st ? props.st.video.packets + props.st.audio.packets : 0
    const p = all ? (n * 100) / all : 0
    return p >= 10 ? p.toFixed(0) : p >= 0.1 ? p.toFixed(1) : p > 0 ? '<0.1' : '0'
}
const ever = computed(() => !!props.st && !props.st.since.startsWith('0001'))
const live = computed(() => !!props.st?.connected)
const tone = { good: 'bg-ok', fair: 'bg-warn', poor: 'bg-bad' } as const

// the return feed, switched here (saved to the input, as in its form): the
// picture and sound the caster gets, and which pair of its channels they
// hear (production's mix-minus; a change reaches them without a reconnect)
const input = computed(() => node.config?.inputs.find((i) => i.id === props.inputId))
const returnChoices = computed(() => (node.config?.inputs ?? []).filter((i) => i.id !== props.inputId))
const leaders = computed(() => returnLeaders(node.config?.inputs ?? [], props.inputId))
const nameOf = (id: string) => node.config?.inputs.find((i) => i.id === id)?.name ?? id
const saving = ref<'' | 'input' | 'pair'>('')
async function setReturn(what: 'input' | 'pair', e: Event) {
    const cur = input.value
    if (!cur) return
    const v = (e.target as HTMLSelectElement).value
    const set = (caster: NonNullable<Input['caster']>) => {
        if (what === 'input') return withReturnChoice(caster, v)
        caster.return_pair = Number(v) || undefined
        return caster
    }
    saving.value = what
    try {
        await node.patch<Input>('inputs', cur.id, (c) => ({ ...c, caster: set({ ...c.caster }) }))
        // said in the status bar, which keeps it: nothing here comes and goes
        node.notify(
            what === 'pair'
                ? 'Return saved: the caster hears it in a moment'
                : 'Return saved: the feed starts in a few seconds',
        )
    } catch {
        // node.save reported it; the select shows the config as it is
    } finally {
        saving.value = ''
    }
}
// comms on top of the return feed (DECISIONS 2026-10-06 "caster comms"):
// production's talk to the caster, set here, live (the gateway and the
// caster's page take a change without a reconnect)
const commsChoices = computed(() =>
    [...returnChoices.value].sort((a, b) => Number(b.kind === 'audio') - Number(a.kind === 'audio')),
)
// the channels as one value: "<pair>:<mono>" ("0:" both of 1+2, "0:left" channel 1)
const commsChannels = computed(() => `${input.value?.caster?.comms_pair ?? 0}:${input.value?.caster?.comms_mono ?? ''}`)
const commsSaving = ref(false)
async function setComms(field: 'input' | 'channels' | 'mode' | 'level', e: Event) {
    const cur = input.value
    if (!cur) return
    const v = (e.target as HTMLSelectElement).value
    const set = (caster: NonNullable<Input['caster']>) => {
        if (field === 'input') caster.comms_input = v || undefined
        if (field === 'channels') {
            const [pair, side] = v.split(':')
            caster.comms_pair = Number(pair) || undefined
            caster.comms_mono = (side as '' | 'left' | 'right') || undefined
        }
        if (field === 'mode') caster.comms_mode = v === 'duck' ? undefined : (v as 'mix' | 'split')
        if (field === 'level') caster.comms_level_db = Number(v) || undefined
        return caster
    }
    commsSaving.value = true
    try {
        await node.patch<Input>('inputs', cur.id, (c) => ({ ...c, caster: set({ ...c.caster }) }))
    } catch {
        // node.save reported it; the selects show the config as it is
    } finally {
        commsSaving.value = false
    }
}
// talkback (DECISIONS 2026-10-06 "caster talkback"): the caster may hold a
// button to talk to production, off air, onto this input's channels 3+4
const talkSaving = ref(false)
async function setTalkback(e: Event) {
    const cur = input.value
    if (!cur) return
    talkSaving.value = true
    try {
        const talkback = (e.target as HTMLInputElement).checked || undefined
        await node.patch<Input>('inputs', cur.id, (c) => ({ ...c, caster: { ...c.caster, talkback } }))
    } catch {
        // node.save reported it; the box shows the config as it is
    } finally {
        talkSaving.value = false
    }
}
// pause the return feed live (no reconnect), or resume it
const pausing = ref(false)
async function togglePause() {
    pausing.value = true
    try {
        await api.casterControl(props.inputId, { return: !!props.st?.return_paused })
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        pausing.value = false
    }
}
const returnName = computed(() => returnChoices.value.find((i) => i.id === input.value?.caster?.return_input)?.name)

// a player input (DECISIONS 2026-10-22 "player inputs"): every player
// connected, each made ready here (their devices, their caps) and taken on
// air, at their next key frame; nothing goes back to them
const players = computed(() => !!input.value?.caster?.players)
const taking = ref('')
async function take(p: CasterStats) {
    if (!p.session) return
    taking.value = p.session
    try {
        await api.casterTake(props.inputId, p.session)
    } catch (e) {
        node.notify(e instanceof Error ? e.message : String(e), true)
    } finally {
        taking.value = ''
    }
}
</script>

<template>
    <div class="mt-3 rounded-md border border-line text-xs" data-test="caster-panel">
        <!-- who is connected, how, and the line as the caster's page reads it -->
        <div class="flex flex-wrap items-center gap-x-2 gap-y-1 px-2.5 pt-2 pb-1.5">
            <b class="text-[13px] font-semibold">{{ players ? 'Players' : 'Caster' }}</b>
            <span v-if="error" class="text-bad">{{ error }}</span>
            <span
                v-else-if="live"
                class="inline-flex min-w-28 items-center gap-1 rounded-full bg-ok/15 px-2 py-0.5 font-semibold text-ok tabular-nums"
                ><i class="inline-block h-1.5 w-1.5 rounded-full bg-ok" /> live {{ ago(st!.since, node.now) }}</span
            >
            <span v-else class="rounded-full bg-bg-3 px-2 py-0.5 text-fg-2"
                >waiting{{ ever ? ' · last ' + ago(st!.since, node.now) + ' ago' : '' }}</span
            >
            <span v-if="live" class="text-fg-2">{{ st!.relay ? 'through central (TURN)' : 'direct' }}</span>
        </div>
        <!-- the line as the caster's page reads it, on a line of its own: at
             the end of the row above, its changing reason wrapped and
             unwrapped every few seconds -->
        <div
            v-if="live"
            class="-mt-1 flex h-5 items-center gap-1 truncate px-2.5 pb-1 text-fg"
            title="the caster's page's own reading of its line"
            data-test="caster-health"
        >
            <template v-if="st!.page?.health">
                <i class="inline-block h-2 w-2 shrink-0 rounded-full" :class="tone[st!.page.health]" />
                <span class="font-semibold">{{ st!.page.health }} line</span>
                <span v-if="st!.page.health_why" class="truncate text-fg-2">· {{ st!.page.health_why }}</span>
            </template>
            <span v-else class="text-fg-2">line: waiting for the caster's page</span>
        </div>

        <!-- the microphone and cues -->
        <section v-if="live" class="border-t border-line px-2.5 py-2">
            <CasterControl :st="st!" :input-id="inputId" part="live" :peak="peak" />
        </section>

        <!-- a player input's players: one on air, the others made ready -->
        <section v-if="players" class="border-t border-line px-2.5 py-2" data-test="players">
            <div class="mb-1.5 subhead">Players · {{ st?.players?.length ?? 0 }} connected</div>
            <div v-if="!st?.players?.length" class="text-fg-2">
                nobody connected: each player opens their invite link and connects
            </div>
            <div
                v-for="p in st?.players ?? []"
                :key="p.session"
                class="mt-1.5 rounded-md border p-2 first:mt-0"
                :class="p.on_air ? 'border-bad' : 'border-line'"
                data-test="player"
            >
                <div class="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <b class="font-semibold">{{ p.name || 'a link with no name' }}</b>
                    <i
                        v-if="p.page?.health"
                        class="inline-block h-2 w-2 shrink-0 rounded-full"
                        :class="tone[p.page.health]"
                        :title="p.page.health + ' line' + (p.page.health_why ? ': ' + p.page.health_why : '')"
                    />
                    <span class="text-fg-2 tabular-nums">{{
                        p.width ? `${p.width}×${p.height} · ${Math.round(p.fps ?? 0)} fps` : 'no picture yet'
                    }}</span>
                    <span class="ml-auto">
                        <span
                            v-if="p.on_air"
                            class="rounded-full bg-bad px-2 py-0.5 font-semibold text-white"
                            data-test="player-on-air"
                            >ON AIR</span
                        >
                        <span v-else-if="p.taking" class="text-warn">taking at their next key frame…</span>
                        <button
                            v-else
                            class="btn-primary px-2 py-0.5 text-xs"
                            :disabled="!!taking || node.readOnly"
                            :title="
                                'Put ' +
                                (p.name || 'this player') +
                                ' on air: the one on air stays until their picture is ready'
                            "
                            data-test="player-take"
                            @click="take(p)"
                        >
                            {{ taking === p.session ? 'Taking…' : 'Take' }}
                        </button>
                    </span>
                </div>
                <div class="mt-1.5">
                    <CasterControl :st="p" :input-id="inputId" part="sending" :session="p.session" />
                </div>
            </div>
        </section>

        <!-- what the caster sees and hears -->
        <section v-if="input && !players" class="border-t border-line px-2.5 py-2">
            <div class="mb-1.5 subhead">Return feed</div>
            <div class="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-1.5">
                <label class="m-0 text-xs text-fg-2"
                    >picture and sound
                    <select
                        :value="returnChoice(input.caster)"
                        :disabled="!!saving"
                        data-test="return-input"
                        @change="setReturn('input', $event)"
                    >
                        <option value="">none</option>
                        <optgroup v-if="leaders.length" label="the same as another caster's">
                            <option v-for="i in leaders" :key="i.id" :value="'caster:' + i.id">
                                same as {{ i.name }}
                            </option>
                        </optgroup>
                        <optgroup label="an input">
                            <option v-for="i in returnChoices" :key="i.id" :value="i.id">{{ i.name }}</option>
                        </optgroup>
                    </select></label
                >
                <label class="m-0 text-xs text-fg-2"
                    >their mix
                    <select
                        :value="input.caster?.return_pair ?? 0"
                        :disabled="!!saving || !input.caster?.return_input"
                        data-test="return-pair"
                        @change="setReturn('pair', $event)"
                    >
                        <option v-for="k in 8" :key="k" :value="k - 1">ch {{ 2 * k - 1 }}+{{ 2 * k }}</option>
                    </select></label
                >
            </div>
            <div class="mt-1 flex min-h-5 flex-wrap items-center gap-x-2 text-xs" role="status">
                <button
                    v-if="input.caster?.return_input"
                    class="px-1.5 py-0 text-xs"
                    :class="st?.return_paused ? 'border-warn text-warn' : ''"
                    :disabled="pausing"
                    :title="
                        st?.return_paused
                            ? 'Send the return feed to the caster again'
                            : 'Stop sending the return feed for now: the caster stays connected'
                    "
                    data-test="return-pause"
                    @click="togglePause"
                >
                    {{ pausing ? '…' : st?.return_paused ? 'Resume' : 'Pause' }}
                </button>
                <span v-if="saving" class="text-fg-2">saving…</span>
                <span v-else-if="!input.caster?.return_input" class="text-fg-2"
                    >the caster sees and hears nothing from the studio</span
                >
                <span v-else-if="st?.return_paused" class="text-warn"
                    >paused: the caster gets nothing from the studio</span
                >
                <span v-else-if="live && st!.return_refused" class="text-warn"
                    >the caster's page was opened before the return feed was set: ask them to reload it</span
                >
                <span v-else-if="live && st!.return_live" class="text-ok">sending {{ returnName }} to the caster</span>
                <span v-else-if="live" class="text-warn">{{ returnName }} is not reaching the caster yet</span>
            </div>
            <div
                v-if="input.caster?.return_input && returnEncoder"
                class="mt-1 text-xs text-fg-3"
                data-test="return-encoder"
            >
                encoded with {{ returnEncoder
                }}<template v-if="returnSharedWith?.length">
                    · one encode shared with {{ returnSharedWith.map(nameOf).join(', ') }}</template
                >
            </div>
        </section>

        <!-- production's comms on top of the return feed -->
        <section v-if="input && !players" class="border-t border-line px-2.5 py-2" data-test="comms">
            <div class="mb-1.5 subhead">Comms</div>
            <div class="grid grid-cols-2 gap-1.5">
                <label class="m-0 text-xs text-fg-3"
                    >from
                    <select
                        :value="input.caster?.comms_input ?? ''"
                        :disabled="commsSaving"
                        data-test="comms-input"
                        @change="setComms('input', $event)"
                    >
                        <option value="">none</option>
                        <option v-for="i in commsChoices" :key="i.id" :value="i.id">
                            {{ i.name }}{{ i.kind === 'audio' ? ' (audio)' : '' }}
                        </option>
                    </select></label
                >
                <label class="m-0 text-xs text-fg-3"
                    >channels
                    <select
                        :value="commsChannels"
                        :disabled="commsSaving || !input.caster?.comms_input"
                        data-test="comms-channels"
                        @change="setComms('channels', $event)"
                    >
                        <template v-for="k in 8" :key="k">
                            <option :value="`${k - 1}:`">ch {{ 2 * k - 1 }}+{{ 2 * k }}</option>
                            <option :value="`${k - 1}:left`">ch {{ 2 * k - 1 }} (mono)</option>
                            <option :value="`${k - 1}:right`">ch {{ 2 * k }} (mono)</option>
                        </template>
                    </select></label
                >
                <label class="m-0 text-xs text-fg-3"
                    >over the programme
                    <select
                        :value="input.caster?.comms_mode || 'duck'"
                        :disabled="commsSaving || !input.caster?.comms_input"
                        data-test="comms-mode"
                        @change="setComms('mode', $event)"
                    >
                        <option value="duck">ducked: the programme drops while they talk</option>
                        <option value="mix">mixed: both at their levels</option>
                        <option value="split">split: comms left ear, programme right</option>
                    </select></label
                >
                <label class="m-0 text-xs text-fg-3"
                    >level
                    <select
                        :value="input.caster?.comms_level_db ?? 0"
                        :disabled="commsSaving || !input.caster?.comms_input"
                        data-test="comms-level"
                        @change="setComms('level', $event)"
                    >
                        <option v-for="db in [-18, -12, -9, -6, -3, 0, 3, 6, 9, 12]" :key="db" :value="db">
                            {{ db > 0 ? '+' : '' }}{{ db }} dB
                        </option>
                    </select></label
                >
            </div>
            <div class="mt-1 text-xs" role="status">
                <span v-if="commsSaving" class="text-fg-3">saving…</span>
                <span v-else-if="!input.caster?.comms_input" class="text-fg-2">no comms to the caster</span>
                <span v-else-if="live && !st?.comms" class="text-warn"
                    >the caster's page takes no comms (opened before them): ask them to reload it</span
                >
                <span v-else-if="live && st?.comms_live" class="text-ok">comms reaching the caster</span>
                <span v-else-if="live" class="text-warn">comms not reaching the caster yet</span>
                <span v-else class="text-fg-2">set: the caster hears them once connected</span>
            </div>
            <label class="mt-1.5 flex items-center gap-1.5 text-xs" data-test="talkback">
                <input
                    type="checkbox"
                    :checked="!!input.caster?.talkback"
                    :disabled="talkSaving"
                    @change="setTalkback"
                />
                talkback: the caster may talk to you, off air, on channels 3+4
            </label>
            <div v-if="input.caster?.talkback" class="mt-0.5 text-xs" role="status" data-test="talkback-status">
                <span v-if="talkSaving" class="text-fg-3">saving…</span>
                <span v-else-if="live && st?.page?.talking" class="font-semibold text-warn"
                    >the caster is talking back now</span
                >
                <span v-else-if="live && !st?.talkback_live" class="text-warn"
                    >the caster's page has no talkback (opened before it): ask them to reload it</span
                >
                <span v-else-if="live" class="text-ok">ready: the caster holds the button to talk</span>
                <span v-else class="text-fg-2">the caster gets the button once connected</span>
            </div>
        </section>

        <!-- what the caster sends: caps, devices, encoder -->
        <section v-if="live && !players" class="border-t border-line px-2.5 py-2">
            <div class="mb-1.5 subhead">The caster's picture</div>
            <CasterControl :st="st!" :input-id="inputId" part="sending" />
        </section>

        <!-- the link in figures -->
        <details v-if="st && ever" open class="border-t border-line px-2.5 py-1.5">
            <summary class="my-0 subhead">
                Connection ·
                <span class="inline-block min-w-9 text-right tabular-nums">{{ st.rtt_ms.toFixed(0) }} ms</span>
                round trip ·
                <span class="tabular-nums" :class="lostForGood ? 'text-bad' : ''">{{ count(lostForGood) }} lost</span>
            </summary>
            <div class="mt-1.5 grid grid-cols-3 gap-x-3.5 gap-y-1">
                <Figure
                    v-if="st.remote"
                    class="col-span-2"
                    label="from"
                    title="the caster's address as this node sees it"
                    :full="st.remote"
                    >{{ st.remote }}</Figure
                >
                <Figure label="round trip">{{ st.rtt_ms.toFixed(1) }} ms</Figure>
                <Figure
                    label="resend window"
                    title="how long a missing packet may hold the stream while it is resent (from the round trip)"
                    >{{ st.wait_ms.toFixed(0) }} ms</Figure
                >
                <Figure
                    label="recovered"
                    :title="`packets that came late or were lost and came back in time: resent (${count(st.video.nacks ?? 0)} asked for) or, for sound, rebuilt from the next packet`"
                    >{{ count(resent) }}<template #sub>{{ pct(resent) }} %</template></Figure
                >
                <Figure
                    label="lost for good"
                    title="packets given up on: they did not come within the resend window; missing in the picture or sound"
                    ><span :class="lostForGood ? 'text-bad' : ''">{{ count(lostForGood) }}</span></Figure
                >
                <Figure label="longest hold" title="the longest a missing packet held the stream while it was resent"
                    >{{ maxHold.toFixed(0) }} ms</Figure
                >
                <Figure
                    label="playout delay"
                    title="how long the caster's picture and sound wait for a resend before they play: none on a clean line; once it loses packets, a round trip and a little, so a resent packet plays in its turn instead of freezing the picture"
                    >{{ (st.video.delay_ms ?? 0).toFixed(0) }} ms</Figure
                >
                <Figure
                    label="key frames asked"
                    title="key frames asked of the caster's browser: at the start and after a loss"
                    >{{ st.key_frame_requests }}</Figure
                >
                <Figure v-if="st.codec" label="video" :full="st.codec">{{ st.codec }}</Figure>
            </div>
        </details>

        <!-- invite links (central) -->
        <details class="border-t border-line px-2.5 py-2" open>
            <summary class="my-0 subhead">
                Invite links{{ invites?.length ? ' · ' + invites.filter(usable).length + ' active' : '' }}
            </summary>
            <div v-if="!nodeId" class="mt-1 text-fg-2">
                made on central: open this node from the fleet page to invite a caster
            </div>
            <template v-else>
                <TurnCheck />
                <form class="mt-2 flex flex-wrap items-center gap-1.5" @submit.prevent="invite">
                    <input
                        v-model="forWho"
                        required
                        maxlength="100"
                        placeholder="who it is for"
                        class="min-w-0 flex-1 text-xs"
                    />
                    <select v-model.number="days" class="w-auto text-xs" aria-label="Valid for">
                        <option :value="1">1 day</option>
                        <option :value="3">3 days</option>
                        <option :value="7">a week</option>
                        <option :value="30">30 days</option>
                    </select>
                    <button class="btn-primary text-xs" title="The link is copied as it is made">Make link</button>
                </form>
                <table v-if="invites?.length" class="mt-2 w-full text-xs">
                    <tr
                        v-for="i in invites"
                        :key="i.id"
                        class="border-t border-line"
                        :class="usable(i) ? '' : 'text-fg-3'"
                    >
                        <td class="py-1.5 pr-2 font-medium">{{ i.name }}</td>
                        <td class="pr-2 text-fg-2">
                            {{ i.revoked_at ? 'revoked' : usable(i) ? 'until ' + until(i.expires_at) : 'expired'
                            }}{{ i.last_used_at ? ' · used ' + ago(i.last_used_at, node.now) + ' ago' : '' }}
                        </td>
                        <td class="py-1.5 text-right whitespace-nowrap">
                            <template v-if="usable(i)">
                                <button class="btn-ghost px-2 py-0.5 text-xs" @click="node.copy(i.url)">
                                    Copy link
                                </button>
                                <button class="ml-1 btn-danger px-2 py-0.5 text-xs" @click="revoke(i)">Revoke</button>
                            </template>
                        </td>
                    </tr>
                </table>
            </template>
        </details>

        <!-- local links (the node's own HTTPS port, no central) -->
        <CasterLocalLinks :input-id="inputId" />
    </div>
</template>
